/**
 * MedicalTab - Onglet Médecine du travail
 * Gestion des visites médicales des employés
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { 
  Plus, Stethoscope, Calendar, MapPin, User, FileText, 
  Check, X, AlertCircle, Loader2, Clock, Upload, Trash2, Edit2
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import {
  MEDICAL_VISIT_TYPE_OPTIONS,
  MEDICAL_VISIT_STATUS_OPTIONS,
  MEDICAL_FITNESS_OPTIONS,
  getMedicalVisitTypeLabel,
  getMedicalVisitStatusLabel,
  getMedicalFitnessLabel,
  getMedicalVisitStatusColor,
  getMedicalFitnessColor,
} from "../../../lib/hr-constants";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MedicalTab({ employee }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    visit_type: "",
    scheduled_date: "",
    scheduled_time: "",
    location: "",
    doctor_name: "",
    notes: "",
  });
  
  // Edit form state (includes result fields)
  const [editForm, setEditForm] = useState({
    visit_type: "",
    scheduled_date: "",
    scheduled_time: "",
    location: "",
    doctor_name: "",
    status: "",
    fitness_result: "",
    restrictions: "",
    next_visit_date: "",
    notes: "",
    completed_date: "",
  });

  const token = localStorage.getItem("token");

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/hr/employees/${employee.id}/medical-visits`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVisits(response.data.visits || []);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des visites médicales");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [employee.id, token]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const handleAddVisit = async () => {
    if (!form.visit_type || !form.scheduled_date) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employee.id}/medical-visits`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAddDialog(false);
      setForm({
        visit_type: "",
        scheduled_date: "",
        scheduled_time: "",
        location: "",
        doctor_name: "",
        notes: "",
      });
      fetchVisits();
    } catch (err) {
      console.error("Error creating visit:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditVisit = async () => {
    if (!selectedVisit) return;
    
    setSaving(true);
    try {
      const updateData = {};
      Object.keys(editForm).forEach(key => {
        if (editForm[key] !== "" && editForm[key] !== null) {
          updateData[key] = editForm[key];
        }
      });
      
      await axios.put(
        `${API}/api/hr/employees/${employee.id}/medical-visits/${selectedVisit.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditDialog(false);
      setSelectedVisit(null);
      fetchVisits();
    } catch (err) {
      console.error("Error updating visit:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVisit = async () => {
    if (!selectedVisit) return;
    
    setSaving(true);
    try {
      await axios.delete(
        `${API}/api/hr/employees/${employee.id}/medical-visits/${selectedVisit.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteDialog(false);
      setSelectedVisit(null);
      fetchVisits();
    } catch (err) {
      console.error("Error deleting visit:", err);
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (visit) => {
    setSelectedVisit(visit);
    setEditForm({
      visit_type: visit.visit_type || "",
      scheduled_date: visit.scheduled_date || "",
      scheduled_time: visit.scheduled_time || "",
      location: visit.location || "",
      doctor_name: visit.doctor_name || "",
      status: visit.status || "",
      fitness_result: visit.fitness_result || "",
      restrictions: visit.restrictions || "",
      next_visit_date: visit.next_visit_date || "",
      notes: visit.notes || "",
      completed_date: visit.completed_date || "",
    });
    setShowEditDialog(true);
  };

  // Separate visits by status
  const upcomingVisits = visits.filter(v => v.status === "scheduled" || v.status === "postponed");
  const pastVisits = visits.filter(v => v.status === "completed" || v.status === "cancelled" || v.status === "no_show");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p>{error}</p>
        <Button variant="outline" onClick={fetchVisits} className="mt-4">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with add button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Médecine du travail</h3>
          <Badge variant="secondary">{visits.length} visite(s)</Badge>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-medical-visit-btn">
          <Plus className="w-4 h-4 mr-2" />
          Planifier une visite
        </Button>
      </div>

      {/* Upcoming visits */}
      {upcomingVisits.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              Visites à venir ({upcomingVisits.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {upcomingVisits.map(visit => (
                <VisitCard 
                  key={visit.id} 
                  visit={visit} 
                  onEdit={() => openEditDialog(visit)}
                  onDelete={() => { setSelectedVisit(visit); setShowDeleteDialog(true); }}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Past visits */}
      {pastVisits.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              Historique ({pastVisits.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-3">
                {pastVisits.map(visit => (
                  <VisitCard 
                    key={visit.id} 
                    visit={visit} 
                    onEdit={() => openEditDialog(visit)}
                    onDelete={() => { setSelectedVisit(visit); setShowDeleteDialog(true); }}
                  />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {visits.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center">
              <Stethoscope className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune visite médicale</h3>
              <p className="text-sm text-slate-500 mb-4">
                Planifiez la première visite médicale pour cet employé
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Planifier une visite
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Planifier une visite médicale</DialogTitle>
            <DialogDescription>
              Renseignez les informations de la visite
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de visite *</Label>
              <Select value={form.visit_type} onValueChange={(v) => setForm({...form, visit_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {MEDICAL_VISIT_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <DatePickerFr
                  value={form.scheduled_date}
                  onChange={(v) => setForm({...form, scheduled_date: v})}
                  placeholder="JJ/MM/AAAA"
                />
              </div>
              <div className="space-y-2">
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={form.scheduled_time}
                  onChange={(e) => setForm({...form, scheduled_time: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({...form, location: e.target.value})}
                placeholder="Adresse ou nom du centre"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Médecin</Label>
              <Input
                value={form.doctor_name}
                onChange={(e) => setForm({...form, doctor_name: e.target.value})}
                placeholder="Dr. ..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Informations complémentaires..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleAddVisit} disabled={saving || !form.visit_type || !form.scheduled_date}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Planifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la visite médicale</DialogTitle>
            <DialogDescription>
              Mettez à jour les informations et le résultat
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de visite</Label>
                <Select value={editForm.visit_type} onValueChange={(v) => setEditForm({...editForm, visit_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDICAL_VISIT_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDICAL_VISIT_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date prévue</Label>
                <DatePickerFr
                  value={editForm.scheduled_date}
                  onChange={(v) => setEditForm({...editForm, scheduled_date: v})}
                />
              </div>
              <div className="space-y-2">
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={editForm.scheduled_time}
                  onChange={(e) => setEditForm({...editForm, scheduled_time: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input
                value={editForm.location}
                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Médecin</Label>
              <Input
                value={editForm.doctor_name}
                onChange={(e) => setEditForm({...editForm, doctor_name: e.target.value})}
              />
            </div>
            
            <hr className="my-4" />
            <h4 className="font-medium text-slate-900">Résultat de la visite</h4>
            
            <div className="space-y-2">
              <Label>Date de réalisation</Label>
              <DatePickerFr
                value={editForm.completed_date}
                onChange={(v) => setEditForm({...editForm, completed_date: v})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Avis d'aptitude</Label>
              <Select value={editForm.fitness_result} onValueChange={(v) => setEditForm({...editForm, fitness_result: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner l'avis..." />
                </SelectTrigger>
                <SelectContent>
                  {MEDICAL_FITNESS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(editForm.fitness_result === "fit_with_restrictions" || editForm.fitness_result === "temporarily_unfit") && (
              <div className="space-y-2">
                <Label>Restrictions / Observations</Label>
                <Textarea
                  value={editForm.restrictions}
                  onChange={(e) => setEditForm({...editForm, restrictions: e.target.value})}
                  placeholder="Détail des restrictions..."
                  rows={2}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Prochaine visite prévue</Label>
              <DatePickerFr
                value={editForm.next_visit_date}
                onChange={(v) => setEditForm({...editForm, next_visit_date: v})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button onClick={handleEditVisit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette visite ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera définitivement la visite médicale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVisit} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Visit Card Component
function VisitCard({ visit, onEdit, onDelete }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors group">
      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
        <Stethoscope className="w-5 h-5 text-blue-600" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-medium text-slate-900">
            {getMedicalVisitTypeLabel(visit.visit_type)}
          </span>
          <Badge variant="outline" className={getMedicalVisitStatusColor(visit.status)}>
            {getMedicalVisitStatusLabel(visit.status)}
          </Badge>
          {visit.fitness_result && (
            <Badge variant="outline" className={getMedicalFitnessColor(visit.fitness_result)}>
              {getMedicalFitnessLabel(visit.fitness_result)}
            </Badge>
          )}
        </div>
        
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {formatDateFr(visit.scheduled_date)}
            {visit.scheduled_time && ` à ${visit.scheduled_time}`}
          </span>
          {visit.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {visit.location}
            </span>
          )}
          {visit.doctor_name && (
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {visit.doctor_name}
            </span>
          )}
        </div>
        
        {visit.restrictions && (
          <p className="text-sm text-amber-600 mt-1">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
            {visit.restrictions}
          </p>
        )}
        
        {visit.next_visit_date && (
          <p className="text-xs text-slate-400 mt-1">
            Prochaine visite : {formatDateFr(visit.next_visit_date)}
          </p>
        )}
      </div>
      
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} data-testid={`edit-visit-${visit.id}`}>
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={onDelete} data-testid={`delete-visit-${visit.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
